import { default as AddIcon } from '@mui/icons-material/Add'
import { default as DeleteIcon } from '@mui/icons-material/Delete'
import { Box, Button, Card, CardContent, Grid, IconButton, Paper, Typography } from '@mui/material'
import { useMemo } from 'react'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import ImageUploadWithHeightAdjustment from 'src/mentor/components/MentorPrescriptionTemplates/components/ImageUploadWithHeightAdjustment/ImageUploadWithHeightAdjustment'
import useHeaderContentHandlers from 'src/mentor/components/MentorPrescriptionTemplates/components/hooks/useHeaderContentHandlers'
import TextPropertiesEditor from 'src/mentor/components/MentorPrescriptionTemplates/components/sections/TextPropertiesEditor'

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
]

export default function MultipleTemplateHeadersComponent({
  prescriptionTemplate,
  prescriptionTemplateHandlers,
  disabled = false,
}) {
  const { setPrescriptionTemplate } = prescriptionTemplateHandlers

  const {
    handleHeaderColumnChange,
    handleTextItemChange,
    handleAddTextItem,
    handleDeleteTextItem,
    handleAddColumn,
    handleDeleteColumn,
    createTextItemSetter,
  } = useHeaderContentHandlers(setPrescriptionTemplate)

  const headerColumns = prescriptionTemplate?.header?.headerColumns || []

  // Memoize text item setters to prevent recreating functions on every render
  const textItemSetters = useMemo(() => {
    const setters = {}
    headerColumns.forEach((column, colIdx) => {
      if (column.type === 'text' && column.text) {
        column.text.forEach((_, textIdx) => {
          const key = `${colIdx}-${textIdx}`
          setters[key] = createTextItemSetter(colIdx, textIdx)
        })
      }
    })
    return setters
  }, [headerColumns, createTextItemSetter])

  return (
    <>
      <Grid item xs={12}>
        <h5>Header Columns</h5>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {headerColumns.map((column, columnIdx) => (
            <Grid item xs={12} key={columnIdx}>
              <Card elevation={2}>
                <CardContent>
                  {/* Column header with type selector and delete button */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Column {columnIdx + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteColumn(columnIdx)}
                      disabled={disabled}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <CustomSelect
                    id="type"
                    label="Type"
                    value={column.type}
                    onChangeEvent={(e) => handleHeaderColumnChange(e, columnIdx)}
                    selectType={selectTypes.string}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    options={TYPE_OPTIONS}
                    disabled={disabled}
                  />

                  {/* Conditional rendering based on type */}
                  {column.type === 'text' ? (
                    <Box mt={2}>
                      {/* Map over text array */}
                      {column.text?.map((textItem, textIdx) => (
                        <Paper
                          key={textIdx}
                          elevation={1}
                          sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Typography variant="caption" color="textSecondary" fontWeight="medium">
                              Text Item {textIdx + 1}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteTextItem(columnIdx, textIdx)}
                              disabled={disabled || column.text?.length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <CustomInput
                            id="text"
                            label="Content"
                            value={textItem.text || ''}
                            onChange={(e) => handleTextItemChange(e, columnIdx, textIdx)}
                            disabled={disabled}
                            fullWidth
                            multiline
                            rows={2}
                          />

                          <Box mt={2}>
                            <TextPropertiesEditor
                              data={textItem}
                              setData={textItemSetters[`${columnIdx}-${textIdx}`]}
                              label=""
                              disabled={disabled}
                            />
                          </Box>
                        </Paper>
                      ))}

                      {/* Add text item button */}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => handleAddTextItem(columnIdx)}
                        variant="outlined"
                        fullWidth
                        disabled={disabled}
                        sx={{ mt: 1 }}
                      >
                        Add Text Item
                      </Button>
                    </Box>
                  ) : (
                    <Box mt={2}>
                      {/* Image controls */}
                      <ImageUploadWithHeightAdjustment
                        imageId={column.imageId}
                        prescriptionTemplate={prescriptionTemplate}
                        type="header"
                        onComplete={(imageId) => {
                          handleHeaderColumnChange(
                            { target: { id: 'imageId', value: imageId } },
                            columnIdx,
                          )
                        }}
                        disabled={disabled}
                        placeholder="Upload Header Image"
                        showSlider={false}
                      />

                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={6}>
                          <CustomInput
                            id="imagePadding"
                            label="Padding"
                            type="number"
                            value={column.imagePadding || ''}
                            onChange={(e) => handleHeaderColumnChange(e, columnIdx)}
                            disabled={disabled}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <CustomInput
                            id="imagePaddingTop"
                            label="Padding Top"
                            type="number"
                            value={column.imagePaddingTop || ''}
                            onChange={(e) => handleHeaderColumnChange(e, columnIdx)}
                            disabled={disabled}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Add column button */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                border: '2px dashed',
                borderColor: 'divider',
                backgroundColor: 'background.default',
                transition: 'all 0.3s ease',
                '&:hover': disabled
                  ? {}
                  : {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                height: '100%',
                minHeight: 200,
              }}
              onClick={disabled ? undefined : handleAddColumn}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  py: 4,
                }}
              >
                <AddIcon fontSize="large" color={disabled ? 'disabled' : 'primary'} />
                <Typography
                  color={disabled ? 'textSecondary' : 'primary'}
                  fontWeight="medium"
                  mt={1}
                >
                  Add New Column
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}
